// ============================================================================
// STOCK POSITION — Inventory slice 2 (ADR-0058 §10).
// Stock quantity and value as at a date, read from the stock movements. Rows
// are per item + location, or per item (all locations together). Server-paged;
// the grand total comes back on every row (sum over the whole report).
// Drill: a row opens the Stock Card for that item (and location) up to the
// as-at date, through the cross-screen navigation seam.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import {
  SyutGrid, type SyutGridColumn, type ActionMenuItem, SyutSelect, type SelectOption,
} from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory, useCan } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useAuthContext } from 'contexts/AuthContext';
import { useNavigation } from 'contexts/NavigationContext';
import { useStockPosition } from 'hooks/api/useInventory';
import {
  stockReportService, itemCategoryService, stockLocationService, classificationService,
} from 'api/services';
import type {
  StockPositionRowDto, StockPositionQueryParams, ClassificationValueDto,
} from 'api/types/inventory.types';
import ReportHeader from 'components/reports/ReportHeader';
import DrillableCell from 'components/reports/DrillableCell';
import { exportToExcel } from 'utils/exportToExcel';

const ListIcon = IconSystem.list;
const ChartIcon = IconSystem.chart;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Local calendar date as yyyy-MM-dd (toISOString would shift across midnight UTC). */
const isoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => isoDate(new Date());
const fmtDate = (s?: string) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString() : '');
const fmtMoney = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Quantities carry up to 6 decimals; trailing zeros are trimmed. */
const fmtQty = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 6 });
const classValueLabel = (v: ClassificationValueDto) => (v.parentName ? `${v.parentName} / ${v.valueName}` : v.valueName);

type GroupBy = 'ITEM_LOCATION' | 'ITEM';
const GROUP_BY_OPTIONS: SelectOption[] = [
  { value: 'ITEM_LOCATION', label: 'Item and location' },
  { value: 'ITEM', label: 'Item' },
];

/** Export loop: pages of 100, capped so a huge catalogue can't hang the browser. */
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_PAGES = 100;

const inp: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };
const filterLabel: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' };
const warnBadge: React.CSSProperties = { background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)' };
const right: React.CSSProperties = { display: 'block', textAlign: 'right' };

/** SyutGrid needs a row id; item + location is unique within one report. */
interface StockPositionRow extends StockPositionRowDto { id: string; }

const isBelowReorder = (r: StockPositionRowDto) => r.reorderLevel != null && r.quantity < r.reorderLevel;

interface AppliedFilters {
  asOf: string;
  groupBy: GroupBy;
  stockLocationId: string;
  businessUnitId: string;
  categoryId: string;
  classificationValueId: string;
  includeZero: boolean;
}

const DEFAULT_FILTERS = (): AppliedFilters => ({
  asOf: today(), groupBy: 'ITEM_LOCATION', stockLocationId: '', businessUnitId: '',
  categoryId: '', classificationValueId: '', includeZero: false,
});

interface StockPositionScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const StockPositionScreen: React.FC<StockPositionScreenProps> = ({ tabId }) => {
  const can = useCan('stock-position');
  const { accessibleBusinessUnits, selectedEntity } = useAuthContext();
  const { openRecord } = useNavigation();

  // ---- Applied filters + paging (the report reflects ONLY what was applied) ----
  const [applied, setApplied] = useState<AppliedFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearchTerm(searchQuery.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const queryParams: StockPositionQueryParams = useMemo(() => ({
    page, pageSize,
    searchTerm: searchTerm || undefined,
    asOf: applied.asOf,
    groupBy: applied.groupBy,
    stockLocationId: applied.stockLocationId || undefined,
    businessUnitId: applied.businessUnitId || undefined,
    categoryId: applied.categoryId || undefined,
    classificationValueId: applied.classificationValueId || undefined,
    includeZero: applied.includeZero || undefined,
  }), [page, pageSize, searchTerm, applied]);

  const { data, loading, error, pagination, refetch } = useStockPosition(queryParams);
  useEffect(() => {
    if (error) {
      const e = error as { response?: { data?: { message?: string } } };
      notificationService.toast.error(e?.response?.data?.message || 'Could not load the stock position.');
    }
  }, [error]);

  // ---- Draft filters (filter panel) ----
  const [filterOpen, setFilterOpen] = useState(false);
  const [fAsOf, setFAsOf] = useState(applied.asOf);
  const [fGroupBy, setFGroupBy] = useState<GroupBy>(applied.groupBy);
  const [fLocationId, setFLocationId] = useState('');
  const [fBuId, setFBuId] = useState('');
  const [fCategoryId, setFCategoryId] = useState('');
  const [fDimId, setFDimId] = useState('');
  const [fValueId, setFValueId] = useState('');
  const [fIncludeZero, setFIncludeZero] = useState(false);

  const locationLabels = useRef<Map<string, string>>(new Map());
  const categoryLabels = useRef<Map<string, string>>(new Map());
  const dimLabels = useRef<Map<string, string>>(new Map());
  const valueLabels = useRef<Map<string, string>>(new Map());

  const buOptions: SelectOption[] = useMemo(() => {
    const list = selectedEntity
      ? accessibleBusinessUnits.filter(b => b.entityId === selectedEntity.id)
      : accessibleBusinessUnits;
    return list.map(b => ({ value: b.id, label: `${b.buCode} - ${b.buName}` }));
  }, [accessibleBusinessUnits, selectedEntity]);

  const loadLocations = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await stockLocationService.lookup({ searchTerm: input || undefined, pageSize: 50 });
      return res.data.map(l => {
        const label = `${l.locationCode} — ${l.locationName}`;
        locationLabels.current.set(l.id, label);
        return { value: l.id, label };
      });
    } catch {
      return [];
    }
  }, []);

  const loadCategories = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await itemCategoryService.getAll({ searchTerm: input || undefined, pageSize: 50 });
      return res.data.map(c => {
        const label = c.path ?? c.categoryName;
        categoryLabels.current.set(c.id, label);
        return { value: c.id, label };
      });
    } catch {
      return [];
    }
  }, []);

  const loadDims = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const q = input.trim().toLowerCase();
      const list = await classificationService.getActive();
      return list
        .filter(d => !q || d.dimensionName.toLowerCase().includes(q) || d.dimensionCode.toLowerCase().includes(q))
        .map(d => {
          dimLabels.current.set(d.id, d.dimensionName);
          return { value: d.id, label: d.dimensionName };
        });
    } catch {
      return [];
    }
  }, []);

  const loadValues = useCallback(async (input: string): Promise<SelectOption[]> => {
    if (!fDimId) return [];
    try {
      const res = await classificationService.getValues({ dimensionId: fDimId, searchTerm: input || undefined, pageSize: 50 });
      return res.data.map(v => {
        const label = classValueLabel(v);
        valueLabels.current.set(v.id, label);
        return { value: v.id, label };
      });
    } catch {
      return [];
    }
  }, [fDimId]);

  const applyFilters = useCallback(() => {
    if (!fAsOf) { notificationService.toast.warning('The as-at date is required.'); return; }
    setApplied({
      asOf: fAsOf, groupBy: fGroupBy, stockLocationId: fLocationId, businessUnitId: fBuId,
      categoryId: fCategoryId, classificationValueId: fValueId, includeZero: fIncludeZero,
    });
    setPage(1);
    setFilterOpen(false);
  }, [fAsOf, fGroupBy, fLocationId, fBuId, fCategoryId, fValueId, fIncludeZero]);

  const clearFilters = useCallback(() => {
    const d = DEFAULT_FILTERS();
    setFAsOf(d.asOf); setFGroupBy(d.groupBy); setFLocationId(''); setFBuId('');
    setFCategoryId(''); setFDimId(''); setFValueId(''); setFIncludeZero(false);
    setApplied(d); setPage(1);
  }, []);

  const appliedCount = [
    applied.groupBy !== 'ITEM_LOCATION', applied.stockLocationId, applied.businessUnitId,
    applied.categoryId, applied.classificationValueId, applied.includeZero,
  ].filter(Boolean).length;

  // ---- Drill: open the Stock Card for the row's item (+ location) up to the as-at date ----
  const openStockCard = useCallback((r: StockPositionRowDto) => {
    const intent = new URLSearchParams();
    if (r.stockLocationId) {
      intent.set('stockLocationId', r.stockLocationId);
      if (r.locationCode || r.locationName) {
        intent.set('locationLabel', [r.locationCode, r.locationName].filter(Boolean).join(' — '));
      }
    }
    intent.set('toDate', applied.asOf);
    openRecord('stock-card', {
      recordId: r.itemId, mode: 'view', label: `${r.itemCode} — ${r.itemName}`, intent: intent.toString(),
    });
  }, [openRecord, applied.asOf]);

  // ---- Export: every page of the applied filters into one workbook ----
  const [exporting, setExporting] = useState(false);
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const rows: StockPositionRowDto[] = [];
      let truncated = false;
      for (let p = 1; p <= EXPORT_MAX_PAGES; p++) {
        const res = await stockReportService.getStockPosition({ ...queryParams, page: p, pageSize: EXPORT_PAGE_SIZE });
        rows.push(...res.data);
        if (!res.pagination.hasNextPage) break;
        if (p === EXPORT_MAX_PAGES) truncated = true;
      }
      if (rows.length === 0) { notificationService.toast.warning('Nothing to export.'); return; }
      const byItem = applied.groupBy === 'ITEM';
      exportToExcel({
        fileName: `stock-position-${applied.asOf}`,
        data: rows.map(r => ({
          'Item code': r.itemCode,
          'Item name': r.itemName,
          Category: r.categoryName ?? '',
          ...(byItem ? {} : { Location: r.locationName ?? r.locationCode ?? '', 'Business unit': r.businessUnitName ?? '' }),
          Quantity: r.quantity,
          Unit: r.baseUomCode ?? '',
          'Average cost': r.averageCost ?? '',
          Value: r.value,
          'Reorder level': r.reorderLevel ?? '',
          'Below reorder': isBelowReorder(r) ? 'Yes' : '',
        })),
      });
      notificationService.toast.success(truncated
        ? `Exported the first ${rows.length} rows — narrow the filters to export the rest.`
        : 'Export downloaded.');
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      notificationService.toast.error(e?.response?.data?.message || 'Could not export the stock position.');
    } finally {
      setExporting(false);
    }
  }, [queryParams, applied.groupBy, applied.asOf]);

  // ---- Grid ----
  const gridRows: StockPositionRow[] = useMemo(
    () => data.map(r => ({ ...r, id: `${r.itemId}:${r.stockLocationId ?? ''}` })),
    [data],
  );
  const grandTotal = data.length > 0 ? data[0].grandTotalValue : 0;
  const groupedByItem = applied.groupBy === 'ITEM';

  const columns: SyutGridColumn<StockPositionRow>[] = useMemo(() => {
    const cols: SyutGridColumn<StockPositionRow>[] = [
      {
        id: 'itemCode', header: 'Item code', size: 130,
        cell: ({ row }) => (
          <DrillableCell onClick={() => openStockCard(row.original)} title="Open the stock card">
            {row.original.itemCode}
          </DrillableCell>
        ),
      },
      { id: 'itemName', header: 'Item name', accessorKey: 'itemName', size: 240 },
      { id: 'category', header: 'Category', size: 160, cell: ({ row }) => row.original.categoryName || '—' },
    ];
    if (!groupedByItem) {
      cols.push(
        {
          id: 'location', header: 'Location', size: 170,
          cell: ({ row }) => row.original.locationName || row.original.locationCode || '—',
        },
        { id: 'businessUnit', header: 'Business unit', size: 150, cell: ({ row }) => row.original.businessUnitName || '—' },
      );
    }
    cols.push(
      {
        id: 'quantity', header: 'Quantity', align: 'right', size: 140,
        cell: ({ row }) => (
          <span style={right}>
            {fmtQty(row.original.quantity)}
            {row.original.baseUomCode && (
              <span style={{ color: 'var(--text-muted)', marginLeft: '0.3rem', fontSize: '0.75rem' }}>{row.original.baseUomCode}</span>
            )}
          </span>
        ),
      },
      { id: 'averageCost', header: 'Average cost', align: 'right', size: 120, cell: ({ row }) => <span style={right}>{fmtMoney(row.original.averageCost)}</span> },
      {
        id: 'value', header: 'Value', align: 'right', size: 130,
        cell: ({ row }) => (
          <span style={{ ...right, color: row.original.value < 0 ? 'var(--danger-color, #dc2626)' : undefined }}>
            {fmtMoney(row.original.value)}
          </span>
        ),
      },
      {
        id: 'reorder', header: 'Reorder', size: 130,
        cell: ({ row }) => {
          const r = row.original;
          if (!isBelowReorder(r)) return null;
          const tip = groupedByItem
            ? `Below the reorder level of ${fmtQty(r.reorderLevel)}`
            : `Below the reorder level of ${fmtQty(r.reorderLevel)} at this location — the level applies to the item's total stock; group by item to compare the total.`;
          return <span className="badge" style={warnBadge} title={tip}>Below reorder</span>;
        },
      },
    );
    return cols;
  }, [groupedByItem, openStockCard]);

  const rowActions = useCallback((r: StockPositionRow): ActionMenuItem[] => [
    { id: 'stock-card', label: 'Stock card', icon: <ListIcon size={18} />, onClick: () => openStockCard(r) },
  ], [openStockCard]);

  const actionButtons: ActionButton[] = [
    actionButtonFactory.refresh(refetch),
    ...(can('EXPORT') ? [{ ...actionButtonFactory.export_(handleExport), disabled: exporting || data.length === 0 }] : []),
  ];

  const lbl = (map: React.MutableRefObject<Map<string, string>>, id: string) => (id ? (map.current.get(id) || 'Selected') : 'All');
  const buLabel = applied.businessUnitId
    ? (buOptions.find(o => o.value === applied.businessUnitId)?.label as string | undefined) || 'Selected'
    : 'All';

  return (
    <EnterpriseScreenShell
      title="Stock Position"
      description="Stock quantity and value as at a date, from the stock movements"
      icon={<ChartIcon size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search item code / name...' }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                  As at <span className="text-danger">*</span>
                </label>
                <input type="date" className="form-input" value={fAsOf} onChange={e => setFAsOf(e.target.value)} style={inp} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Group by" size="sm" options={GROUP_BY_OPTIONS} isClearable={false}
                  value={fGroupBy} onChange={v => setFGroupBy(((v as string) || 'ITEM_LOCATION') as GroupBy)}
                  menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Location" size="sm" isAsync loadOptions={loadLocations} defaultOptions cacheOptions={false}
                  seedOptions={fLocationId ? [{ value: fLocationId, label: locationLabels.current.get(fLocationId) || '' }] : undefined}
                  value={fLocationId} onChange={v => setFLocationId((v as string) || '')}
                  placeholder="All locations" isClearable menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Business unit" size="sm" options={buOptions}
                  value={fBuId} onChange={v => setFBuId((v as string) || '')}
                  placeholder="All business units" isClearable menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Category" size="sm" isAsync loadOptions={loadCategories} defaultOptions cacheOptions={false}
                  seedOptions={fCategoryId ? [{ value: fCategoryId, label: categoryLabels.current.get(fCategoryId) || '' }] : undefined}
                  value={fCategoryId} onChange={v => setFCategoryId((v as string) || '')}
                  placeholder="All (sub-categories included)" isClearable menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Classification" size="sm" isAsync loadOptions={loadDims} defaultOptions cacheOptions={false}
                  seedOptions={fDimId ? [{ value: fDimId, label: dimLabels.current.get(fDimId) || '' }] : undefined}
                  value={fDimId} onChange={v => { setFDimId((v as string) || ''); setFValueId(''); }}
                  placeholder="Choose a dimension" isClearable menuPortalTarget={document.body} />
                {fDimId && (
                  <div style={{ marginTop: '0.35rem' }}>
                    <SyutSelect key={fDimId} size="sm" isAsync loadOptions={loadValues} defaultOptions cacheOptions={false}
                      seedOptions={fValueId ? [{ value: fValueId, label: valueLabels.current.get(fValueId) || '' }] : undefined}
                      value={fValueId} onChange={v => setFValueId((v as string) || '')}
                      placeholder="Choose a value" isClearable menuPortalTarget={document.body} />
                  </div>
                )}
              </div>
              <div className="filter-section">
                <label style={filterLabel}>
                  <input type="checkbox" checked={fIncludeZero} onChange={e => setFIncludeZero(e.target.checked)} /> Include zero rows
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader
        filters={[
          { label: 'As at', value: fmtDate(applied.asOf) },
          { label: 'Group by', value: groupedByItem ? 'Item' : 'Item and location' },
          { label: 'Location', value: applied.stockLocationId ? lbl(locationLabels, applied.stockLocationId) : 'All' },
          { label: 'Business unit', value: buLabel },
          { label: 'Category', value: lbl(categoryLabels, applied.categoryId) },
          ...(applied.classificationValueId
            ? [{ label: 'Classification', value: lbl(valueLabels, applied.classificationValueId) }]
            : []),
        ]}
      />
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
        border: '1px solid var(--border-color)', borderTop: 'none',
        borderRadius: '0 0 0.5rem 0.5rem', overflow: 'hidden',
      }}>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <SyutGrid<StockPositionRow>
            data={gridRows}
            columns={columns}
            enableSorting={false}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={page - 1} pageSize={pageSize}
            onPageChange={(i) => setPage(i + 1)}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No stock for these filters.'}
          />
        </div>
        <div style={{
          padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-color)',
          fontSize: '0.8125rem', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ color: 'var(--text-secondary)' }}>Total value</span>
          <span>{fmtMoney(grandTotal)}</span>
        </div>
      </div>
    </EnterpriseScreenShell>
  );
};

export default StockPositionScreen;
